import { defineStore } from 'pinia';
import { ref } from 'vue';
import client from '@/api/client';

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  emailVerified: boolean;
  /** True when email is the `steam_<id>@placeholder.local` stub from Steam-only signup. */
  emailIsPlaceholder: boolean;
  role: string;
  steamId: string | null;
  avatarUrl: string | null;
  tradeUrl: string | null;
  walletAvailableCents: number;
  walletHeldCents: number;
  kycStatus: 'NONE' | 'PENDING' | 'APPROVED';
  kycLastChangedAt: string | null;
  billingInfo: BillingInfo | null;
  createdAt: string;
}

export interface BillingInfo {
  firstName: string;
  lastName: string;
  /** ISO 3166-1 alpha-2 country code. */
  country: string;
  /** Street / house / apartment. */
  address: string;
  city: string | null;
  postalCode: string;
  phone: string | null;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const loading = ref(false);
  const initialized = ref(false);

  async function fetchUser(): Promise<void> {
    loading.value = true;
    try {
      const { data } = await client.get<AuthUser>('/users/me');
      user.value = data;
    } catch {
      user.value = null;
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  async function logout(): Promise<void> {
    try {
      await client.post('/auth/logout');
    } catch {
      // ignore errors — we clear state regardless
    } finally {
      user.value = null;
    }
  }

  function isAuthenticated(): boolean {
    return user.value !== null;
  }

  return { user, loading, initialized, fetchUser, logout, isAuthenticated };
});
